import React, { useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreRoundedIcon from '@material-ui/icons/RestoreRounded';
import { Formik } from 'formik';
import { softDeleteAssignment, trackerMetadataUpdate } from 'sdk';
import { cutText } from 'utils';

import { ReactComponent as ArrownDownIcon } from 'pages/Teachers/Assignments/CreateMath/assets/arrow-down-icon.svg';
import { ReactComponent as CalendarIcon } from 'pages/Teachers/Assignments/CreateMath/assets/calendar-icon.svg';
import { ReactComponent as PersonIcon } from 'pages/Teachers/Assignments/CreateMath/assets/person-icon.svg';
import RubricSelector from 'pages/Teachers/Assignments/CreateMath/components/RubricSelector';
import {
  restoreAssignment,
  updateAssignmentDueDate,
  updateAssignmentOpenDate
} from 'pages/Teachers/Assignments/Detail/sdk';
import { assignmentAssign } from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyError, notifyErrors, notifySuccess } from 'utils/notifications';

import DateTimeField from 'components/DateTimeField';

import styles from './styles.module.scss';

const ELA_INSIGHTS_ENABLED =
  process.env.REACT_APP_ELA_INSIGHTS_ENABLED === 'true';

export const AssignmentEditModal = ({
  subject,
  open,
  sections,
  assignmentDetails,
  assignmentDetailsRefetch,
  onClose
}) => {
  const [openDatePickerId, setOpenDatePickerId] = useState(null);
  const [dueDatePickerId, setDueDatePickerId] = useState(null);
  const [selectedRubric, setSelectedRubric] = useState('');
  const [maxGradeValue, setMaxGradeValue] = useState(100);
  const [newSections, setNewSections] = useState([]);

  const { trackEvent } = useEdLightTracking({
    feature: 'edit assignment',
    view: 'assignment'
  });

  const assignments = useMemo(() => {
    const instances = assignmentDetails?.instances || [];
    return instances.flatMap((instance) =>
      (instance.assignments || []).map((assignment) => ({
        ...assignment,
        instanceId: instance.id
      }))
    );
  }, [assignmentDetails]);

  useEffect(() => {
    const maxValue = assignmentDetails?.tasks[0]?.max_value || 100;
    setMaxGradeValue(maxValue);
  }, [assignmentDetails]);

  const restore = async ({ assignmentId }) => {
    const { success } = await restoreAssignment(assignmentId);

    trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
      form: 'edit assignment',
      button: 'restore class',
      assignment_id: assignmentId
    });

    if (success) {
      notifySuccess('Assignment restored.');
      assignmentDetailsRefetch();
    } else {
      notifyError('Failed to restore assignment.');
    }
  };

  const softDelete = async ({ assignmentId }) => {
    const { success } = await softDeleteAssignment(assignmentId);

    trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
      form: 'edit assignment',
      button: 'archive class',
      assignment_id: assignmentId
    });

    if (success) {
      notifySuccess('Assignment deleted.');
      assignmentDetailsRefetch();
    } else {
      notifyError('Failed to delete assignment.');
    }
  };

  const updateOpenDate = async ({ assignmentId, openDate }) => {
    const { success, errors } = await updateAssignmentOpenDate(
      assignmentId,
      openDate
    );

    if (success) {
      notifySuccess('Assignment open date successfully updated.');
      assignmentDetailsRefetch();
    } else {
      notifyErrors(errors);
    }
  };

  const updateDueDate = async ({ assignmentId, dueDate }) => {
    const { success, errors } = await updateAssignmentDueDate(
      assignmentId,
      dueDate
    );

    if (success) {
      notifySuccess('Assignment due date successfully updated.');
      assignmentDetailsRefetch();
    } else {
      notifyErrors(errors);
    }
  };

  const handleClassSelect = (e) => {
    const sectionId = Number(e.target.value);
    const section = sections.find((s) => s.id === sectionId);

    if (section && !newSections.find((s) => s.id === section.id)) {
      const now = new Date();
      const inTwoDays = new Date();
      inTwoDays.setDate(now.getDate() + 2);

      setNewSections([
        ...newSections,
        {
          ...section,
          open_at: now.toISOString(),
          due_at: inTwoDays.toISOString()
        }
      ]);
    }
    e.target.value = '';
  };

  const removeNewSection = ({ id }) => {
    setNewSections(newSections.filter((s) => s.id !== id));
  };

  const handleNewSectionDateChange = ({ id, field, date }) => {
    setNewSections(
      newSections.map((s) => (s.id === id ? { ...s, [field]: date } : s))
    );
  };

  const handleSaveAssignmentDetails = async ({
    updatedAssignmentValues,
    newSections
  }) => {
    const payload = new FormData();

    if (updatedAssignmentValues.name) {
      payload.append('name', updatedAssignmentValues.name);
    }
    if (updatedAssignmentValues.description !== undefined) {
      payload.append('description', updatedAssignmentValues.description);
    }

    if (selectedRubric) {
      payload.append('value_type', selectedRubric.key);
    }

    if (maxGradeValue) {
      payload.append('custom_max', maxGradeValue);
    }

    const { success: updateSuccess, errors: updateErrors } =
      await trackerMetadataUpdate(assignmentDetails.id, payload);

    trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
      form: 'edit',
      classes: newSections?.length || 0,
      assignment_id: assignmentDetails.id
    });

    if (!updateSuccess) {
      notifyErrors(updateErrors);
      return;
    }

    if (newSections && newSections.length > 0) {
      const assignmentPayload = {
        sections_data: newSections.map((section) => ({
          section_id: section.id,
          open: section.open_at,
          due: section.due_at
        }))
      };

      trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
        form: 'assign',
        classes: newSections?.length || 0
      });

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(assignmentDetails.id, assignmentPayload);

      if (!assignSuccess) {
        notifyErrors(assignErrors);
        assignmentDetailsRefetch();
        return;
      }
    }

    notifySuccess('Assignment successfully updated.');
    assignmentDetailsRefetch();
    onClose();
  };

  const handleOnSave = async (values, { setSubmitting }) => {
    await handleSaveAssignmentDetails({
      updatedAssignmentValues: values,
      newSections: newSections
    });
    setSubmitting(false);
    onClose();
  };

  useEffect(() => {
    const valueType = assignmentDetails?.tasks[0]?.value_type;

    if (valueType) {
      const matchingOption = VALUES_OPTIONS.find(
        (opt) => opt.key === valueType
      );

      setSelectedRubric(matchingOption || '');
    }
  }, [assignmentDetails?.tasks]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      classes={{ paper: styles.dialogPaper }}
    >
      <DialogTitle disableTypography className={styles.dialogTitle}>
        <Typography className={styles.title}>Edit Assignment</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          className={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Formik
        enableReinitialize
        initialValues={{
          name: assignmentDetails?.name || '',
          description: assignmentDetails?.description || ''
        }}
        validate={(values) => {
          const errors = {};
          if (!values.name) {
            errors.name = 'Required';
          }
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          handleOnSave(values, { setSubmitting });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <div className={styles.inputsContainer}>
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Assignment Name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={styles.inputField}
                  />
                  {touched.name && errors.name ? (
                    <div className={styles.errorText}>{errors.name}</div>
                  ) : null}
                </div>

                <div>
                  <textarea
                    name="description"
                    placeholder="Add a Description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={4}
                    className={styles.inputField}
                  />
                </div>
              </div>

              <RubricSelector
                options={VALUES_OPTIONS}
                selected={selectedRubric}
                onSelect={setSelectedRubric}
                maxGrade={maxGradeValue}
                onMaxGradeChange={(newValue) => setMaxGradeValue(newValue)}
              />
              <hr className={styles.divider} />
              <div className={styles.fieldSection}>
                <div className={styles.sectionHeaderWrapper}>
                  <div className={styles.fieldLabel}>
                    <PersonIcon /> <span>Assigned Classes</span>
                  </div>
                </div>

                {(!ELA_INSIGHTS_ENABLED ||
                  (subject && ELA_INSIGHTS_ENABLED)) && (
                  <div className={styles.selectWrapper}>
                    <select
                      defaultValue=""
                      onChange={handleClassSelect}
                      className={styles.inputField}
                    >
                      <option value="" disabled>
                        Select a class...
                      </option>
                      {sections.map((section) => {
                        const isAlreadyAssigned =
                          assignments.some((a) => a.name === section.name) ||
                          newSections.some((ns) => ns.id === section.id);

                        return (
                          <option
                            key={section.id}
                            value={section.id}
                            disabled={isAlreadyAssigned}
                          >
                            {cutText({
                              text: section.name,
                              char: 55
                            })}
                          </option>
                        );
                      })}
                    </select>
                    <ArrownDownIcon className={styles.chevron} />
                  </div>
                )}

                <div className={styles.selectedSectionsList}>
                  {assignments.map((assignment) => (
                    <div
                      key={`assigned-${assignment.id}`}
                      className={`${styles.selectedSectionCard} ${
                        assignment.deleted ? styles.deletedCard : ''
                      }`}
                    >
                      <div className={styles.sectionCardHeader}>
                        <span
                          className={`${styles.sectionName} ${
                            assignment.deleted ? styles.deletedText : ''
                          }`}
                        >
                          {assignment.name || 'Unnamed Class'}
                        </span>

                        {!assignment.deleted ? (
                          <button
                            type="button"
                            onClick={() =>
                              softDelete({ assignmentId: assignment.id })
                            }
                            className={styles.removeSectionBtn}
                          >
                            <DeleteIcon className={styles.deleteIcon} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              restore({ assignmentId: assignment.id })
                            }
                            className={styles.removeSectionBtn}
                          >
                            <RestoreRoundedIcon />
                          </button>
                        )}
                      </div>

                      <div className={styles.dateControls}>
                        <DateTimeField
                          id={`open-${assignment.id}`}
                          value={assignment.open_at}
                          disabled={assignment.deleted}
                          onChange={(date) =>
                            updateOpenDate({
                              assignmentId: assignment.id,
                              openDate: date
                            })
                          }
                          open={openDatePickerId === `open-${assignment.id}`}
                          onClose={() => setOpenDatePickerId(null)}
                          TextFieldComponent={({ value }) => (
                            <button
                              type="button"
                              disabled={assignment.deleted}
                              onClick={() =>
                                setOpenDatePickerId(`open-${assignment.id}`)
                              }
                              className={styles.dateButton}
                            >
                              {value ? `Open: ${value}` : 'Add Open Time'}{' '}
                              <CalendarIcon />
                            </button>
                          )}
                        />

                        <DateTimeField
                          id={`due-${assignment.id}`}
                          value={assignment.due_at}
                          disabled={assignment.deleted}
                          minDate={assignment.open_at}
                          onChange={(date) =>
                            updateDueDate({
                              assignmentId: assignment.id,
                              dueDate: date
                            })
                          }
                          open={dueDatePickerId === `due-${assignment.id}`}
                          onClose={() => setDueDatePickerId(null)}
                          TextFieldComponent={({ value }) => (
                            <button
                              type="button"
                              disabled={assignment.deleted}
                              onClick={() =>
                                setDueDatePickerId(`due-${assignment.id}`)
                              }
                              className={styles.dateButton}
                            >
                              {value ? `Due: ${value}` : 'Add Due Date'}{' '}
                              <CalendarIcon />
                            </button>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                  {assignments.length === 0 && newSections.length === 0 && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      className={styles.emptyStateText}
                    >
                      No classes assigned.
                    </Typography>
                  )}

                  {newSections.map((section) => (
                    <div
                      key={`new-${section.id}`}
                      className={styles.selectedSectionCard}
                    >
                      <div className={styles.sectionCardHeader}>
                        <span className={styles.sectionName}>
                          {section.name || 'Unnamed Class'}
                          <span className={styles.pendingSection}>
                            {' '}
                            (Pending Save)
                          </span>
                        </span>

                        <button
                          type="button"
                          onClick={() => removeNewSection({ id: section.id })}
                          className={styles.removeSectionBtn}
                        >
                          <CloseIcon />
                        </button>
                      </div>

                      <div className={styles.dateControls}>
                        <DateTimeField
                          id={`new-open-${section.id}`}
                          value={section.open_at}
                          onChange={(date) =>
                            handleNewSectionDateChange({
                              id: section.id,
                              field: 'open_at',
                              date: date
                            })
                          }
                          open={openDatePickerId === `new-open-${section.id}`}
                          onClose={() => setOpenDatePickerId(null)}
                          TextFieldComponent={({ value }) => (
                            <button
                              type="button"
                              onClick={() =>
                                setOpenDatePickerId(`new-open-${section.id}`)
                              }
                              className={styles.dateButton}
                            >
                              {value ? `Open: ${value}` : 'Add Open Time'}{' '}
                              <CalendarIcon />
                            </button>
                          )}
                        />

                        <DateTimeField
                          id={`new-due-${section.id}`}
                          value={section.due_at}
                          minDate={section.open_at}
                          onChange={(date) =>
                            handleNewSectionDateChange({
                              id: section.id,
                              field: 'due_at',
                              date: date
                            })
                          }
                          open={dueDatePickerId === `new-due-${section.id}`}
                          onClose={() => setDueDatePickerId(null)}
                          TextFieldComponent={({ value }) => (
                            <button
                              type="button"
                              onClick={() =>
                                setDueDatePickerId(`new-due-${section.id}`)
                              }
                              className={styles.dateButton}
                            >
                              {value ? `Due: ${value}` : 'Add Due Date'}{' '}
                              <CalendarIcon />
                            </button>
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>

            <DialogActions className={styles.footer}>
              <Button
                type="submit"
                disabled={isSubmitting}
                className={styles.saveButton}
              >
                SAVE CHANGES
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};
